import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle, Link2 } from "lucide-react";
import { toast } from "sonner";

const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

// Resilient mapper — supports both DB shapes ({q, a} from AI generator and {question, answer} from manual entry)
const normalize = (item) => ({
  question: item?.question || item?.q || "",
  answer: item?.answer || item?.a || "",
});

export const FAQSection = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  // Auto-expand FAQ matching URL hash (e.g., #faq-how-long-to-go-live) on load
  useEffect(() => {
    if (!items || items.length === 0) return;
    const hash = (typeof window !== "undefined" ? window.location.hash : "") || "";
    if (!hash.startsWith("#faq-")) return;
    const targetSlug = hash.slice(5);
    const idx = items.findIndex((it) => slugify(normalize(it).question) === targetSlug);
    if (idx >= 0) {
      setOpenIndex(idx);
      // Smooth scroll to the FAQ after a short delay
      setTimeout(() => {
        const el = document.getElementById(`faq-${targetSlug}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [items]);

  if (!items || items.length === 0) return null;

  const copyAnchorLink = (slug, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#faq-${slug}`;
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(url).then(() => toast.success("FAQ link copied"));
    }
    // Update browser URL without reload
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", `#faq-${slug}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="faq-section">
      {items.map((rawItem, index) => {
        const item = normalize(rawItem);
        const slug = slugify(item.question) || `q-${index}`;
        const anchorId = `faq-${slug}`;
        const isOpen = openIndex === index;
        return (
          <motion.div
            key={anchorId}
            id={anchorId}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="rounded-xl border border-slate-200 overflow-hidden bg-white scroll-mt-24"
          >
            {/* Question button (also acts as backlink target) */}
            <button
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
              data-testid={`faq-question-${index}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-4 h-4 text-amber-600" />
                </div>
                <span className="font-medium text-slate-900">{item.question}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Copy link to this FAQ"
                  onClick={(e) => copyAnchorLink(slug, e)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyAnchorLink(slug, e); } }}
                  className="opacity-50 hover:opacity-100 p-1 rounded hover:bg-slate-100"
                  data-testid={`faq-copy-link-${index}`}
                  title="Copy link"
                >
                  <Link2 className="w-4 h-4 text-slate-500" />
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                />
              </div>
            </button>

            {/* Answer */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="px-4 pb-4 pt-0">
                    <div className="pl-11 pr-4 py-3 rounded-lg bg-slate-50 text-slate-700 text-sm leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};
